/**
 * Mapping of user IDs to funny titles
 */
export const funnyTitles: Record<string, string> = {
  // Data Intake & Monitoring team
  "60de375f7536500070c0e5e4": "Mr. SMRT", // Tomasz Karelus
  "557058:af337879-595d-4c58-adb7-b8701ed4f93c": "The Alley Cat", // Jade Martin
  "557058:ca4fe2d6-7382-4b4a-9444-345014cd9632": "The Spider Master", // John Michael Canete
  "557058:aa3923ea-4732-4247-a195-7b462b2764d4": "The Artiste", // Carlos Ayala

  // DataLocker team
  "712020:41e756f5-09a5-4b40-a6c2-f0b7568a7493": "Mr. Nice", // Karol Kopicera
  "61c263cce76379006847e835": "Hip Hop Dancer", // Edyta Szkiłądź
  "5ba29133eb3f0c2f90816d7f": "Bart", // Bart Liszka
  "557058:0ae3e448-3937-4e50-b3bd-596eb33f8cdd": "Smooth Operator", // Vlad

  // CasePortal and NetSuite team
  "605e1553570829006a0e101a": "The Prince", // Tomasz Skwarek
  "712020:3b14c7c1-e5f9-4621-828c-157107eac008": "Mr. Perfect", // nikolam
  "63a27eba4bc858b303cba5d7": "WordPress King", // Matt Mortek
  "5c9ea1a8a54a69118b32348a": "The K Man", // Konrad Bogacki
  "712020:57f7788b-53f0-4b2b-8a8b-ec5a6ab78860": "The New Guy", // Bartłomiej Kesicki
  "712020:2f714a9d-b3eb-4f98-914a-b976d4a38a79": "Little Wolf", // Vukasin Davidovic

  // Additional fun titles
  "557058:5b62aa24-c690-43f8-a161-1860abaf5e04": "The Tech Guy", // Andrew Bodis
  "712020:bf5eca57-6194-47e3-9542-5c6f7b1d5c7d": "Product Manager", // Alex Huitink
  "557058:fcd3f25a-715a-4819-a248-0d8498d51dd4": "Product Guru", // Suzanne Abate

  // Default for anyone not specifically mapped
  "default": "Code Gladiator"
}

// Short ID mapping - for handling different ID formats
// Maps short IDs to full account IDs
const shortIdMap: Record<string, string> = {
  // Create a reverse mapping of short IDs to full account IDs
  // This will be populated dynamically when needed
};

// Populate shortIdMap with short versions of the account IDs
Object.keys(funnyTitles).forEach(id => {
  if (id !== 'default') {
    // For IDs with namespace:uuid format, extract just the uuid part
    const parts = id.split(':');
    if (parts.length > 1) {
      shortIdMap[parts[1]] = id;
    }
    
    // Also add the last 8 characters as a potential match
    if (id.length > 8) {
      shortIdMap[id.substring(id.length - 8)] = id;
    }
  }
});

/**
 * Competitor interface for user objects that can have funny titles
 */
interface Competitor {
  id?: string;
  accountId?: string;
  role?: string;
  name?: string;
  // Allow arbitrary additional properties while remaining type-safe
  [key: string]: unknown;
}

/**
 * Helper function to get title for a competitor
 * @param competitor - User object with id and role
 * @param useFunnyTitles - Whether to use funny titles or return the role
 * @returns The appropriate title for the competitor
 */
export const getFunnyTitle = (competitor: Competitor, useFunnyTitles = true): string => {
  if (!useFunnyTitles) return competitor.role || 'Team Member';
  
  // Try direct match first
  if (competitor.id && funnyTitles[competitor.id]) {
    return funnyTitles[competitor.id];
  }
  
  // Try to match with accountId if available
  if (competitor.accountId && funnyTitles[competitor.accountId]) {
    return funnyTitles[competitor.accountId];
  }
  
  // Try to match using the short ID mapping
  // Check if this ID might be a partial of a full ID we know
  if (!competitor.id && !competitor.accountId) {
    return funnyTitles.default;
  }
  
  // Try with ID first if available
  if (competitor.id) {
    const parts = competitor.id.split(':');
    const potentialShortId = parts.length > 1 ? parts[1] : competitor.id;
    
    // Try the UUID part if it's a namespaced ID
    if (shortIdMap[potentialShortId]) {
      const fullId = shortIdMap[potentialShortId];
      return funnyTitles[fullId];
    }
    
    // Try the last 8 characters as a last resort
    if (competitor.id.length > 8) {
      const shortId = competitor.id.substring(competitor.id.length - 8);
      if (shortIdMap[shortId]) {
        const fullId = shortIdMap[shortId];
        return funnyTitles[fullId];
      }
    }
  }
  
  // Try with accountId if available and ID didn't match
  if (competitor.accountId) {
    const parts = competitor.accountId.split(':');
    const potentialShortId = parts.length > 1 ? parts[1] : competitor.accountId;
    
    // Try the UUID part if it's a namespaced ID
    if (shortIdMap[potentialShortId]) {
      const fullId = shortIdMap[potentialShortId];
      return funnyTitles[fullId];
    }
    
    // Try the last 8 characters as a last resort
    if (competitor.accountId.length > 8) {
      const shortId = competitor.accountId.substring(competitor.accountId.length - 8);
      if (shortIdMap[shortId]) {
        const fullId = shortIdMap[shortId];
        return funnyTitles[fullId];
      }
    }
  }
  
  // No match found, return default
  return funnyTitles.default;
};
